import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Truck, Camera, MessageCircle, CreditCard, Clock, Heart, ArrowRight, Phone, Mail } from "lucide-react";
import { CITIES, COUNTRIES } from "@/cityConfig";
import Logo from "@/components/Logo";
import CitySearchCombobox from "@/components/CitySearchCombobox";
import BouquetLeadForm from "@/components/BouquetLeadForm";
import ReviewsMarquee from "@/components/ReviewsMarquee";
import { getLangFromUrl, setLangInUrl, useAutoSyncLangToUrl, updateCanonical, useHreflang } from "@/utils/seoLang";

const allCities = Object.values(CITIES);

const citiesByCountry = allCities.reduce((acc, city) => {
  if (!acc[city.country]) acc[city.country] = [];
  acc[city.country].push(city);
  return acc;
}, {});

export default function CitySelector() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [lang, setLangState] = useState(() => getLangFromUrl(searchParams, ["ru", "en"]));
  const [selectedSlug, setSelectedSlug] = useState("");
  const [articles, setArticles] = useState([]);

  // If lang was auto-detected, push it into URL so it's shareable & canonical-stable
  useAutoSyncLangToUrl(lang, searchParams, setSearchParams);

  // Self-referential canonical + hreflang for the homepage (otherwise ?lang=en
  // keeps the static homepage canonical and EN home is treated as a duplicate of RU).
  useHreflang("/", ["ru", "en"]);
  useEffect(() => { updateCanonical("/"); }, [lang]);

  const setLang = (newLang) => {
    setLangState(newLang);
    setLangInUrl(newLang, searchParams, setSearchParams);
  };

  useEffect(() => {
    fetch("/api/blog/posts?limit=6")
      .then(r => {
        if (!r.ok) throw new Error("Failed");
        return r.json();
      })
      .then(data => {
        const list = data?.articles || [];
        setArticles(list.slice(0, 6));
      })
      .catch(() => {});
  }, []);

  const texts = {
    ru: {
      hero: "Закажите цветы по всему миру",
      sub: "Доставим свежий букет в день заказа. Выберите ваш город.",
      btn: "Перейти к каталогу",
      placeholder: "Выберите город доставки",
      howTitle: "Как мы работаем",
      steps: [
        { icon: MessageCircle, title: "Напишите нам", desc: "В WhatsApp или Telegram — выберите букет из каталога или опишите пожелания" },
        { icon: Camera, title: "Фото перед доставкой", desc: "Флорист соберёт букет и отправит вам фото для согласования" },
        { icon: Truck, title: "Доставка в день заказа", desc: "Курьер доставит букет по адресу — домой, в отель, ресторан или офис" },
        { icon: Heart, title: "Эмоции гарантированы", desc: "Получатель получит свежий букет и вашу открытку с тёплыми словами" },
      ],
      whyTitle: "Почему выбирают YUCCA Flowers",
      whyItems: [
        { icon: Clock, title: "Доставка от 1 часа", desc: "Свежие букеты с доставкой в день заказа по всем городам присутствия" },
        { icon: CreditCard, title: "Оплата из любой страны", desc: "Принимаем карты всех банков мира, криптовалюту и переводы" },
        { icon: Camera, title: "Фото до и после", desc: "Покажем букет перед отправкой и пришлём фото момента вручения" },
        { icon: MessageCircle, title: "Мультиязычная поддержка", desc: "Менеджеры говорят на русском, английском и турецком языках" },
      ],
      notInList: "Нет вашего города? Напишите нам — мы сможем вам помочь",
      countries: "Наши города",
      premiumEyebrow: "ПРЕМИУМ-НАПРАВЛЕНИЯ",
      premiumTitle: "Флористика для важных событий",
      premiumSubtitle: "Полное оформление под ключ. Бодрум, Анталья, Дубай, Барселона.",
      premiumServices: [
        {
          slug: "weddings",
          name: "Свадьбы",
          desc: "Букет невесты, арка, банкет, фотозона. От €2 500.",
          status: "active",
        },
        {
          slug: "yachts",
          name: "Оформление яхт",
          desc: "Цветы на яхту: предложения, дни рождения, чартеры. От €300.",
          status: "active",
        },
        {
          slug: "events",
          name: "Корпоративы и ивенты",
          desc: "Гала-ужины, конференции, открытия, годовщины брендов.",
          status: "soon",
        },
        {
          slug: "private",
          name: "Частные праздники",
          desc: "Дни рождения, юбилеи, помолвки, годовщины.",
          status: "soon",
        },
      ],
      premiumCta: "Открыть",
      premiumSoonLabel: "СКОРО",
      blogTitle: "Интересное в нашем блоге",
      blogAll: "Все статьи",
      blogRead: "Читать",
    },
    en: {
      hero: "Order flowers worldwide",
      sub: "Fresh bouquets delivered same day. Choose your city.",
      btn: "Go to catalog",
      placeholder: "Choose delivery city",
      howTitle: "How we work",
      steps: [
        { icon: MessageCircle, title: "Message us", desc: "On WhatsApp or Telegram — choose from catalog or describe your wishes" },
        { icon: Camera, title: "Photo before delivery", desc: "Our florist creates the bouquet and sends you a photo for approval" },
        { icon: Truck, title: "Same-day delivery", desc: "Courier delivers to the address — home, hotel, restaurant, or office" },
        { icon: Heart, title: "Emotions guaranteed", desc: "Recipient gets a fresh bouquet and your card with warm words" },
      ],
      whyTitle: "Why choose YUCCA Flowers",
      whyItems: [
        { icon: Clock, title: "Delivery from 1 hour", desc: "Fresh bouquets with same-day delivery in all our cities" },
        { icon: CreditCard, title: "Payment from any country", desc: "We accept cards from any bank worldwide, crypto, and transfers" },
        { icon: Camera, title: "Photos before & after", desc: "We show the bouquet before sending and send a photo of delivery" },
        { icon: MessageCircle, title: "Multilingual support", desc: "Our managers speak Russian, English, and Turkish" },
      ],
      notInList: "Don't see your city? Message us — we can help",
      countries: "Our cities",
      premiumEyebrow: "PREMIUM SERVICES",
      premiumTitle: "Floristry for the moments that matter",
      premiumSubtitle: "Full turnkey design. Bodrum, Antalya, Dubai, Barcelona.",
      premiumServices: [
        {
          slug: "weddings",
          name: "Weddings",
          desc: "Bridal bouquet, arch, banquet, photo zone. From €2,500.",
          status: "active",
        },
        {
          slug: "yachts",
          name: "Yacht Flowers",
          desc: "Flowers for yachts: proposals, birthdays, charters. From €300.",
          status: "active",
        },
        {
          slug: "events",
          name: "Corporate & events",
          desc: "Gala dinners, conferences, brand openings, anniversaries.",
          status: "soon",
        },
        {
          slug: "private",
          name: "Private celebrations",
          desc: "Birthdays, jubilees, engagements, anniversaries.",
          status: "soon",
        },
      ],
      premiumCta: "Explore",
      premiumSoonLabel: "COMING SOON",
      blogTitle: "From our blog",
      blogAll: "All articles",
      blogRead: "Read",
    },
  };

  const tx = texts[lang] || texts.ru;

  const handleGo = () => {
    if (selectedSlug) navigate(`/${selectedSlug}${lang !== "ru" ? `?lang=${lang}` : ""}`);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="city-selector-page">
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4 max-w-6xl mx-auto">
        <Logo variant="light" size="medium" />
        <div className="flex items-center gap-2">
          <Link
            to={`/weddings${lang !== "ru" ? `?lang=${lang}` : ""}`}
            className="hidden sm:inline-flex items-center gap-1.5 mr-2 px-3.5 py-1.5 text-[11px] tracking-[0.18em] font-medium text-[#D4B896] hover:text-white border border-[#C9A87C]/40 hover:border-[#C9A87C] rounded-full transition-all"
            data-testid="header-weddings-link"
          >
            {lang === "en" ? "WEDDINGS" : "СВАДЬБЫ"}
          </Link>
          <Link
            to={`/yachts${lang !== "ru" ? `?lang=${lang}` : ""}`}
            className="hidden sm:inline-flex items-center gap-1.5 mr-2 px-3.5 py-1.5 text-[11px] tracking-[0.18em] font-medium text-[#D4B896] hover:text-white border border-[#C9A87C]/40 hover:border-[#C9A87C] rounded-full transition-all"
            data-testid="header-yachts-link"
          >
            {lang === "en" ? "YACHTS" : "ЯХТЫ"}
          </Link>
          <div className="inline-flex bg-white/10 rounded-full p-0.5 border border-white/10">
            {["ru", "en"].map(l => (
              <button
                key={l}
                onClick={() => setLang(l)}
                className={`px-3.5 py-1 rounded-full text-xs font-semibold transition-all ${lang === l ? "bg-white text-black" : "text-white/60 hover:text-white"}`}
                data-testid={`lang-toggle-${l}`}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>
          <a
            href="https://wa.me/905316224378"
            target="_blank"
            rel="noopener noreferrer"
            className="ml-2 text-white/60 hover:text-white transition-colors text-xs font-medium hidden sm:block"
          >
            +90 531 622 43 78
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="relative px-4 pt-12 sm:pt-20 pb-16 sm:pb-24">
        <div className="absolute inset-0 bg-gradient-to-b from-pink-600/10 via-transparent to-transparent pointer-events-none" />
        <div className="relative max-w-2xl mx-auto text-center">
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight"
            style={{ fontFamily: "Playfair Display, serif" }}
          >
            {tx.hero}
          </h1>
          <p className="text-base sm:text-lg text-white/50 mb-10 max-w-md mx-auto">
            {tx.sub}
          </p>

          {/* City search combobox */}
          <CitySearchCombobox lang={lang} selectedSlug={selectedSlug} onSelect={setSelectedSlug} />

          <button
            onClick={handleGo}
            disabled={!selectedSlug}
            className={`px-10 py-3.5 rounded-full text-base font-bold transition-all shadow-lg ${selectedSlug
              ? "bg-gradient-to-r from-pink-500 to-rose-500 text-white hover:from-pink-600 hover:to-rose-600 shadow-pink-500/30 hover:shadow-pink-500/50 cursor-pointer"
              : "bg-white/10 text-white/30 cursor-not-allowed"
            }`}
            data-testid="go-to-catalog-btn"
          >
            {tx.btn}
          </button>
        </div>
      </section>

      {/* Cities Grid */}
      <section className="px-4 pb-16 max-w-5xl mx-auto">
        <h2
          className="text-center text-lg font-semibold text-white/40 uppercase tracking-widest mb-10"
        >
          {tx.countries}
        </h2>
        <div className="space-y-8">
          {Object.entries(COUNTRIES).map(([countrySlug, country]) => {
            const cities = citiesByCountry[countrySlug] || [];
            if (!cities.length) return null;
            return (
              <div key={countrySlug}>
                <div className="flex items-center gap-2 mb-3 px-1">
                  <span className="text-base">{country.flag}</span>
                  <span className="text-xs font-bold text-white/30 uppercase tracking-widest">
                    {lang === "en" ? country.nameEn : country.name}
                  </span>
                  <div className="flex-1 h-px bg-white/5 ml-2" />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {cities.map(city => (
                    <button
                      key={city.slug}
                      onClick={() => navigate(`/${city.slug}${lang !== "ru" ? `?lang=${lang}` : ""}`)}
                      className="group bg-white/5 hover:bg-white/10 border border-white/5 hover:border-pink-500/30 rounded-xl p-4 text-center transition-all"
                      data-testid={`city-btn-${city.slug}`}
                    >
                      <div className="text-sm font-semibold text-white group-hover:text-pink-400 transition-colors">
                        {lang === "en" ? city.nameEn : city.name}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* How We Work */}
      <section className="px-4 py-16 sm:py-20 bg-white/[0.02]">
        <div className="max-w-5xl mx-auto">
          <h2
            className="text-center text-2xl sm:text-3xl font-bold text-white mb-12"
            style={{ fontFamily: "Playfair Display, serif" }}
          >
            {tx.howTitle}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {tx.steps.map((step, i) => (
              <div
                key={i}
                className="bg-white/5 border border-white/5 rounded-2xl p-6 text-center hover:bg-white/[0.07] hover:border-pink-500/20 transition-all"
              >
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-pink-500/20">
                  <step.icon className="w-5 h-5 text-white" />
                </div>
                <div className="text-xs font-bold text-pink-400 mb-2">0{i + 1}</div>
                <h3 className="text-sm font-bold text-white mb-2">{step.title}</h3>
                <p className="text-xs text-white/40 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="px-4 py-16 sm:py-20">
        <div className="max-w-5xl mx-auto">
          <h2
            className="text-center text-2xl sm:text-3xl font-bold text-white mb-12"
            style={{ fontFamily: "Playfair Display, serif" }}
          >
            {tx.whyTitle}
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {tx.whyItems.map((item, i) => (
              <div
                key={i}
                className="flex gap-4 bg-white/5 border border-white/5 rounded-2xl p-5 hover:bg-white/[0.07] hover:border-white/10 transition-all"
              >
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                  <item.icon className="w-5 h-5 text-pink-400" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-white mb-1">{item.title}</h3>
                  <p className="text-xs text-white/40 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Premium B2B Services (Weddings / Events / Private) */}
      <section className="px-4 py-16 sm:py-20" data-testid="home-premium-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-[11px] tracking-[0.35em] text-[#D4B896] mb-3 font-light">{tx.premiumEyebrow}</p>
            <h2
              className="text-2xl sm:text-3xl font-bold text-white mb-3"
              style={{ fontFamily: "Playfair Display, serif" }}
            >
              {tx.premiumTitle}
            </h2>
            <p className="text-sm text-white/50 max-w-xl mx-auto">{tx.premiumSubtitle}</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {tx.premiumServices.map(service => {
              const isActive = service.status === "active";
              const cardClass = isActive
                ? "group relative bg-gradient-to-br from-[#C9A87C]/15 to-white/5 border border-[#C9A87C]/30 hover:border-[#C9A87C] hover:from-[#C9A87C]/25 transition-all rounded-2xl p-5 block"
                : "relative bg-white/5 border border-white/5 rounded-2xl p-5 opacity-70";

              const content = (
                <>
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 rounded-xl bg-[#C9A87C]/20 flex items-center justify-center shrink-0">
                      <Heart className={`w-5 h-5 ${isActive ? "text-[#D4B896]" : "text-white/30"}`} />
                    </div>
                    {!isActive && (
                      <span className="text-[9px] tracking-[0.25em] px-2 py-1 bg-white/10 text-white/50 rounded-sm">
                        {tx.premiumSoonLabel}
                      </span>
                    )}
                  </div>
                  <h3 className={`text-base font-bold mb-1.5 ${isActive ? "text-white" : "text-white/60"}`}>
                    {service.name}
                  </h3>
                  <p className="text-xs text-white/40 leading-relaxed mb-4">{service.desc}</p>
                  {isActive && (
                    <span className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.2em] font-medium text-[#D4B896] group-hover:gap-2.5 transition-all">
                      {tx.premiumCta} <ArrowRight className="w-3 h-3" />
                    </span>
                  )}
                </>
              );

              return isActive ? (
                <Link
                  key={service.slug}
                  to={`/${service.slug}${lang !== "ru" ? `?lang=${lang}` : ""}`}
                  className={cardClass}
                  data-testid={`home-premium-${service.slug}`}
                >
                  {content}
                </Link>
              ) : (
                <div
                  key={service.slug}
                  className={cardClass}
                  data-testid={`home-premium-${service.slug}`}
                >
                  {content}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Blog Section */}
      {articles.length > 0 && (
        <section className="px-4 py-16 sm:py-20 bg-white/[0.02]">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center justify-between mb-10">
              <h2
                className="text-2xl sm:text-3xl font-bold text-white"
                style={{ fontFamily: "Playfair Display, serif" }}
              >
                {tx.blogTitle}
              </h2>
              <Link
                to="/blog"
                className="flex items-center gap-1.5 text-pink-400 hover:text-pink-300 text-sm font-medium transition-colors"
                data-testid="blog-see-all"
              >
                {tx.blogAll} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {articles.map(article => {
                const title = lang === "en" ? (article.title_en || article.title_ru) : article.title_ru;
                const excerpt = lang === "en" ? (article.excerpt_en || article.excerpt_ru) : article.excerpt_ru;
                const cityName = article.city
                  ? (lang === "en" ? CITIES[article.city]?.nameEn : CITIES[article.city]?.name) || ""
                  : "";
                return (
                  <Link
                    key={article.slug}
                    to={`/blog/${article.slug}${lang !== "ru" ? `?lang=${lang}` : ""}`}
                    className="group bg-white/5 border border-white/5 rounded-2xl p-5 hover:bg-white/[0.08] hover:border-pink-500/20 transition-all flex flex-col"
                    data-testid={`blog-card-${article.slug}`}
                  >
                    {cityName && (
                      <span className="text-[10px] font-bold text-pink-400 uppercase tracking-wider mb-2">{cityName}</span>
                    )}
                    <h3 className="text-sm font-bold text-white mb-2 group-hover:text-pink-300 transition-colors leading-snug line-clamp-2">
                      {title}
                    </h3>
                    <p className="text-xs text-white/35 leading-relaxed line-clamp-3 flex-grow">
                      {excerpt}
                    </p>
                    <span className="mt-3 text-xs font-semibold text-pink-400 group-hover:text-pink-300 transition-colors flex items-center gap-1">
                      {tx.blogRead} <ArrowRight className="w-3 h-3" />
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <ReviewsMarquee city="" lang={lang} dark />

      <BouquetLeadForm lang={lang} variant="section" />

      {/* Footer */}
      <footer className="bg-black text-white border-t border-white/5" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 sm:gap-12">
            {/* Brand */}
            <div className="lg:col-span-2">
              <div className="mb-6">
                <Logo variant="light" size="large" />
              </div>
              <p className="text-gray-300 mb-6 max-w-sm text-base leading-relaxed">
                {lang === "en"
                  ? "International flower delivery — Turkey, Spain, Greece, France, USA, Monaco, UAE, Thailand. Fresh bouquets with same-day delivery, photo before sending."
                  : "Международная доставка цветов — Турция, Испания, Греция, Франция, США, Монако, ОАЭ, Таиланд. Свежие букеты с доставкой в день заказа, фото перед отправкой."}
              </p>
              <a
                href="https://wa.me/905316224378"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#25d366] hover:bg-[#20bd5a] text-white px-6 py-3 rounded-full font-bold transition-all hover:scale-105 shadow-lg"
                data-testid="footer-whatsapp-btn"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                <span>{lang === "en" ? "Message on WhatsApp" : "Написать в WhatsApp"}</span>
              </a>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-lg font-bold mb-6 text-pink-400">{lang === "en" ? "Contacts" : "Контакты"}</h4>
              <ul className="space-y-4">
                <li>
                  <a href="tel:+905316224378" className="flex items-center gap-3 text-gray-300 hover:text-white transition-colors">
                    <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                      <Phone className="w-5 h-5 text-pink-400" />
                    </div>
                    <span className="font-medium">+90 531 622 43 78</span>
                  </a>
                </li>
                <li>
                  <a href="mailto:info@yuccaflower.com" className="flex items-center gap-3 text-gray-300 hover:text-white transition-colors">
                    <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-pink-400" />
                    </div>
                    <span className="font-medium">info@yuccaflower.com</span>
                  </a>
                </li>
                <li>
                  <a href="https://t.me/Yucca_flower" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-gray-300 hover:text-white transition-colors">
                    <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                      <MessageCircle className="w-5 h-5 text-pink-400" />
                    </div>
                    <span className="font-medium">Telegram</span>
                  </a>
                </li>
              </ul>
            </div>

            {/* Info */}
            <div>
              <h4 className="text-lg font-bold mb-6 text-pink-400">{lang === "en" ? "Information" : "Информация"}</h4>
              <ul className="space-y-4">
                <li className="flex items-center gap-3 text-gray-300">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-pink-400" />
                  </div>
                  <span className="font-medium">09:00 - 21:00</span>
                </li>
                <li className="text-gray-300 pl-[52px] font-medium">
                  {lang === "en" ? "Same-day delivery" : "Доставка в день заказа"}
                </li>
                <li className="text-gray-300 pl-[52px] font-medium">
                  {lang === "en" ? "Payment in any currency" : "Оплата любой валютой"}
                </li>
                <li className="text-gray-300 pl-[52px] font-medium">
                  {lang === "en" ? "Photo before delivery" : "Фото перед доставкой"}
                </li>
              </ul>
            </div>

            {/* Cities by country (SEO internal linking) */}
            <div data-testid="footer-cities">
              <h4 className="text-lg font-bold mb-6 text-pink-400">{lang === "en" ? "Cities" : "Города"}</h4>
              <ul className="space-y-4 text-sm">
                {Object.values(COUNTRIES).map((country) => {
                  const cities = citiesByCountry[country.slug] || [];
                  if (cities.length === 0) return null;
                  const countryLabel = lang === "en" ? country.nameEn : country.name;
                  return (
                    <li key={country.slug}>
                      <div className="flex items-center gap-1.5 text-gray-400 font-semibold mb-1.5">
                        <span aria-hidden="true">{country.flag}</span>
                        <span>{countryLabel}</span>
                      </div>
                      <ul className="flex flex-wrap gap-x-3 gap-y-1 pl-1">
                        {cities.map((city) => (
                          <li key={city.slug}>
                            <Link
                              to={`/${city.slug}${lang !== "ru" ? `?lang=${lang}` : ""}`}
                              className="text-gray-300 hover:text-pink-400 transition-colors"
                              data-testid={`footer-city-${city.slug}`}
                            >
                              {lang === "en" ? city.nameEn : city.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          {/* Not in list */}
          <div className="mt-10 mb-6 text-center">
            <p className="text-sm text-white/30">{tx.notInList}</p>
          </div>

          <div className="border-t border-white/10 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4 text-center sm:text-left">
            <p className="text-gray-400 text-sm">
              &copy; 2024 YUCCA Flowers. {lang === "en" ? "Made with" : "Сделано с"}{" "}
              <Heart className="w-4 h-4 text-pink-500 fill-pink-500 inline-block align-middle mx-0.5" />
            </p>
            <div className="flex flex-wrap items-center justify-center sm:justify-end gap-x-4 gap-y-2 text-sm max-w-full" data-testid="footer-links">
              <Link to={`/weddings${lang !== "ru" ? `?lang=${lang}` : ""}`} className="text-[#D4B896] hover:text-white transition-colors" data-testid="footer-weddings-link">
                {lang === "en" ? "Weddings" : "Свадьбы"}
              </Link>
              <Link to={`/yachts${lang !== "ru" ? `?lang=${lang}` : ""}`} className="text-[#D4B896] hover:text-white transition-colors" data-testid="footer-yachts-link">
                {lang === "en" ? "Yachts" : "Яхты"}
              </Link>
              <Link to="/blog" className="text-gray-500 hover:text-gray-300 transition-colors" data-testid="footer-blog-link">
                {lang === "en" ? "Blog" : "Блог"}
              </Link>
              <Link to={`/press-about-us${lang !== "ru" ? `?lang=${lang}` : ""}`} className="text-gray-500 hover:text-gray-300 transition-colors" data-testid="footer-press-link">
                {lang === "en" ? "Press" : "Пресса о нас"}
              </Link>
              <Link to="/terms" className="text-gray-500 hover:text-gray-300 transition-colors" data-testid="footer-terms-link">
                {lang === "en" ? "Terms" : "Соглашение"}
              </Link>
              <Link to="/privacy" className="text-gray-500 hover:text-gray-300 transition-colors" data-testid="footer-privacy-link">
                {lang === "en" ? "Privacy" : "Конфиденциальность"}
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
